import _ from "lodash";

/**
 *
 * @enum {string}
 */
export const OverwriteMode = {
    Nothing: "nothing", // 何もしません(trueで返します)
    Update: "update", // データを更新します
    Rewrite: "rewrite", // 元を消して生成し直します
    Error: "error", // 存在していたらエラーにします
}

const MaplibreManager = () => {

    let layerEventList = {}

    /**
     * クライントの地図の中心座標を取得する
     * @param map {Map}
     * @param mapRef {ref} (mapのref)
     * @returns {[double,double]|null}
     */
    const getCenterCoordsFromRef = (map, mapRef) => {
        if (!map || !mapRef.current) { return null }

        // TODO: 地図が画面より小さい場合に要検証
        const centerPoint = [mapRef.current.clientWidth / 2, mapRef.current.clientHeight / 2]
        const centerCoords = map.unproject(centerPoint)

        return [centerCoords.lng, centerCoords.lat]
    }

    /**
     * pointのgeojsonを返す
     * @param point {{lng,lat}|{latitude,longitude}|[number,number]} 座標
     * @param defaultCoordinates {[number,number]} デフォルトの座標(配列のみ)
     * @returns {{data: {features: [{geometry: {coordinates: *, type: string}, type: string}], type: string}, type: string}}
     */
    const pointGeojson = (point, defaultCoordinates = [0,0]) => {
        return {
            type: "geojson",
            data: pointGeojsonData(point, defaultCoordinates)
        }
    }

    /**
     *
     * @param point {{lng,lat}|{latitude,longitude}|[number,number]} 座標
     * @param defaultCoordinates {[number,number]} デフォルトの座標(配列のみ)
     * @returns {{features: [{geometry: {coordinates: [number,number], type: string}, type: string}], type: string}}
     */
    const pointGeojsonData = (point, defaultCoordinates = [0,0]) => {
        let coordinates = null
        if (typeof point === "object") {
            if (_.has(point, "latitude") && _.has(point, "longitude")) {
                coordinates = [point.longitude, point.latitude]
            } else if (_.has(point, "lat") && _.has(point, "lng")) {
                coordinates = [point.lng, point.lat]
            }
        } else if(Array.isArray(point) && point.length >= 2) {
            coordinates = [point[0], point[1]]
        }
        if (!coordinates || !Array.isArray(coordinates) || _.size(coordinates) !== 2) {
            coordinates = defaultCoordinates
        }
        return {
            type: "FeatureCollection",
            features: [
                {
                    type: "Feature",
                    geometry: {
                        type: "Point",
                        coordinates,
                    }
                }
            ]
        }
    }

    /**
     * pointのsourceを追加
     *
     * @param map {Map}
     * @param sourceName {string}
     * @param point {{lng,lat}|{longitude,latitude}|[number,number]}
     * @param overwrite {OverwriteMode} 存在していた場合の動作
     * @param defaultCoordinates {[number,number]}
     * @returns {boolean}
     */
    const addPointSource = (map, sourceName, point, overwrite = OverwriteMode.Nothing, defaultCoordinates = [0,0]) =>
        addGeojsonSource(map, sourceName, pointGeojsonData(point, defaultCoordinates), overwrite)

    /**
     * geojsonのソースを追加
     *
     * @param map {Map}
     * @param sourceName {string}
     * @param data {any}
     * @param overwrite {OverwriteMode}
     * @returns {boolean}
     */
    const addGeojsonSource = (map, sourceName, data, overwrite = OverwriteMode.Nothing) => {
        if (!map) { return false }
        const source = map.getSource(sourceName)
        if (source) {
            switch(overwrite) {
                case OverwriteMode.Nothing:
                    return true
                case OverwriteMode.Error:
                    throw new Error(`Source already exists ${sourceName}`)
                case OverwriteMode.Update:
                    source.setData(data)
                    return true
                case OverwriteMode.Rewrite:
                    map.removeSource(sourceName)
                    break
                default:
                    throw new Error(`Overwrite mode not implementation ${overwrite}`)
            }
        }
        map.addSource(sourceName, {
            type: "geojson",
            data
        })
        return true
    }

    /**
     * vectorのsourceを追加する
     *
     * @param map {Map}
     * @param sourceName {string}
     * @param tiles {[string]}
     * @param overwrite {OverwriteMode}
     * @returns {boolean}
     */
    const addVectorSource = (map, sourceName, tiles, overwrite = OverwriteMode.Nothing) => {
        if (!map) { return false }
        const source = map.getSource(sourceName)
        if (source) {
            switch(overwrite) {
                case OverwriteMode.Nothing:
                    return true
                case OverwriteMode.Error:
                    throw new Error(`Source already exists ${sourceName}`)
                case OverwriteMode.Update:
                    source.setTiles(tiles)
                    break
                case OverwriteMode.Rewrite:
                    map.removeSource(sourceName)
                    break
                default:
                    throw new Error(`Overwrite mode not implemented ${overwrite}`)
            }
        }
        try {
            map.addSource(sourceName, {
                type: "vector",
                tiles: typeof tiles === "string" ? [tiles] : tiles,
            })
        } catch(e) {
            console.log("[MaplibreManager]", "get vector source error", sourceName, e.message)
        }
        return true
    }

    /**
     * rasterのsourceを追加する
     *
     * @param map {Map}
     * @param sourceName {string}
     * @param tiles {[string]}
     * @param overwrite {OverwriteMode}
     * @returns {boolean}
     */
    const addRasterSource = (map, sourceName, tiles, overwrite = OverwriteMode.Nothing) => {
        if (!map) { return false }
        const source = map.getSource(sourceName)
        if (source) {
            switch(overwrite) {
                case OverwriteMode.Nothing:
                    return true
                case OverwriteMode.Error:
                    throw new Error(`Source already exists ${sourceName}`)
                case OverwriteMode.Update:
                    source.setTiles(tiles)
                    return true
                case OverwriteMode.Rewrite:
                    map.removeSource(sourceName)
                    break
                default:
                    throw new Error(`Overrwite mode not implemented ${overwrite}`)
            }
        }
        try {
            map.addSource(sourceName, {
                type: "raster",
                tiles,
            })
        } catch(e) {
            console.log("[MaplibreManager]", "get raster source error", sourceName, e.message)
        }
        return true
    }

    /**
     * レイヤーを追加する
     *
     * @param map {Map}
     * @param layerProperties {object}
     * @param overwrite {OverwriteMode}
     * @returns {boolean}
     */
    const addLayer = (map, layerProperties, overwrite = OverwriteMode.Nothing) => {
        if (!map) { return false }
        let layerId = layerProperties?.id
        if (!layerId) { throw new Error('Layer id was required') }
        if (map.getLayer(layerId)) {
            switch(overwrite) {
                case OverwriteMode.Nothing:
                    return true
                case OverwriteMode.Error:
                    throw new Error(`Layer id already exists ${layerId}`)
                case OverwriteMode.Update:
                case OverwriteMode.Rewrite:
                    map.removeLayer(layerId)
                    break
                default:
                    throw new Error(`Overwrite mode not implemented ${overwrite}`)
            }
        }
        let props = _m(layerProperties)
        console.log(props)
        map.addLayer(props)
        return true
    }

    /**
     * レイヤーを並び替える
     *
     * @param map {Map}
     * @param layerNameArray {[string]|[Object.<string,{id:string,at:number}|string>]}
     */
    const sortLayers = (map, layerNameArray) => {
        if(!map) { return false }
        let layers = []
        for(const l of layerNameArray) {
            if (typeof l === "string") {
                layers.push(l)
            } else if (typeof l === "object") {
                layers = _.concat(layers, parseLayerNames(l))
            }
        }
        console.log("[MaplibreManager]", "sort layers", layers)
        layers.forEach(l => map.getLayer(l) && map.moveLayer(l))
        return true
    }

    /**
     * sourceとlayerを削除する
     * @param map {Map}
     * @param sources {string|[string]|Object.<string,string>}
     * @param layers {string|[string]|Object.<string,string|{id:string,at:number}>}
     */
    const removes = (map, {sources, layers}) => {
        removeClickEvent(map, layers)
        removeLayers(map, layers)
        removeSources(map, sources)
    }

    /**
     * sourceを削除する
     * @param map {Map}
     * @param sourceNames {string|[string]|Object.<string,string>}
     */
    const removeSources = (map, sourceNames) => {
        if (!map) { return false }
        parseSources(sourceNames)
            .forEach(s => map.getSource(s) && map.removeSource(s))
        return true
    }

    /**
     * layerを削除する
     * @param map {Map}
     * @param layerNames {string|[string]|Object.<string,string|{id:string,at:number}>}
     */
    const removeLayers = (map, layerNames) => {
        if (!map) { return false }
        parseLayerNames(layerNames)
            .forEach(l => map.getLayer(l) && map.removeLayer(l))
        return true
    }

    /**
     *
     * @param map {Map}
     * @param layerNames {string|[string]|Object.<string,string|{id:string,at:number}>}
     * @param visible
     * @returns {boolean}
     */
    const setVisible = (map, layerNames, visible) => {
        if (!map) { return false }
        parseLayerNames(layerNames)
            .forEach(l => {
                console.log("Set Visible", l, visible)
                map.getLayer(l) && map.setLayoutProperty(l, "visibility", visible ? "visible" : "none")
            })
        return true
    }

    /**
     * linear zoomを指定する
     * @param zoomValues {Object.<number,number>} zoom:<value>のobject
     * @param type {string}
     */
    const zoomInterpolate = (zoomValues, type = "linear") => {
        let vals = []
        Object.entries(zoomValues).sort((v1, v2) => {
            return parseFloat(v1[0]) - parseFloat(v2[0])
        }).map(([zoom, val]) => {
            vals.push(parseFloat(zoom))
            vals.push(val)
        })

        return [
            "interpolate",
            [type],
            ["zoom"],
            ...vals,
        ]
    }

    /**
     * camelCaseでのプロパティをハイフン型のプロパティに変換する
     *
     * @param properties {{string, any}}
     * @param excludeKeys {[string]}
     * @returns {any}
     * @private
     */
    const _m = (properties, excludeKeys = []) => {
        let newEntries = Object.entries(properties).map(([key, value]) => {
            console.log(key, value)
            if (!Array.isArray(value) && typeof value === "object") { value = _m(value, excludeKeys) }
            if (excludeKeys.includes(key)) { return [key, value]}

            if (key === "minZoom") { return ["minzoom", value] }
            if (key === "maxZoom") { return ["maxzoom", value] }

            if (key.match(/^[a-z][^\-]+[A-Z][^\-]*/)) {
                let m
                while((m = key.search(/[A-Z]/)) !== -1) {
                    let l = key.split("")
                    l[m] = l[m].toLowerCase()
                    l.splice(m, 0, "-")
                    key = l.join("")
                }
            }
            return [key, value]
        })
        console.log(properties, newEntries)
        return Object.fromEntries(newEntries)
    }

    /**
     * [内部用] layerNamesを展開してlayerIdの配列にする
     *
     * @param layerNames
     * @returns {[string]}
     */
    const parseLayerNames = (layerNames) => {
        if (typeof layerNames === "string") {
            return [layerNames]
        } else if (typeof layerNames === "object") {
            return Object.values(layerNames).sort((v1, v2) => v1.at - v2.at).map(l => l.id)
        } else if (Array.isArray(layerNames)) {
            return layerNames
        }
        return []
    }

    /**
     * [内部用] sourceNamesを展開してsourceIdの配列にする
     *
     * @param sourceNames {string|[string]|Object.<string,string>}
     * @returns {[string]}
     */
    const parseSources = (sourceNames) => {
        if (typeof sourceNames === "object") {
            return Object.values(sourceNames)
        } else if (typeof sourceNames === "string") {
            return [sourceNames]
        }
        return []
    }

    /**
     * LayerのClickイベントを除去する
     *
     * @param map {Map}
     * @param layerNames {string|[string]|{id:string,at:number}}
     * @param onClick {function} 指定しなければすべてのイベントを削除(普通はいらない)
     * @returns {boolean}
     */
    const removeClickEvent = (map, layerNames, onClick = null) => {
        if(!map) { return false }
        const layers = parseLayerNames(layerNames)

        if (_.isEmpty(layers)) { return true }

        map.off("click", layers, onClick)
        map.off("mouseenter", layers)
        map.off("mouseleave", layers)
        return true
    }

    /**
     * LayerにClickイベントを追加する
     * OverWriteModeはNothing(追加),Rewrite|Update(付け替え)
     *
     * @param map {Map}
     * @param layerNames {string|[string]|{id:string,at:number}}
     * @param onClick {function}
     * @param overwrite {OverwriteMode}
     * @param hoverPointer {string}
     * @returns {boolean}
     */
    const addClickEvent = (map, layerNames, onClick, overwrite = OverwriteMode.Rewrite, hoverPointer = "pointer") => {
        if (!map) { return false }
        const layers = parseLayerNames(layerNames)

        if (overwrite === OverwriteMode.Error) {
            throw new Error('MaplibreManager not implemented Overwrite Error Mode')
        }

        if(_.isEmpty(layers)) {
            return true
        }

        if ([OverwriteMode.Rewrite, OverwriteMode.Update]) {
            map.off("click", layers)
            map.off("mouseenter", layers)
            map.off("mouseleave", layers)
        }

        const canvas = map.getCanvasContainer()

        map.on("click", layers, onClick)
        map.on("mouseenter", layers, () => {
            canvas.style.cursor = hoverPointer
        })
        map.on("mouseleave", layers, () => {
            canvas.style.cursor = "grab"
        })

        return true
    }

    const setFilter = (map, layerNames, filter) => {
        if (!map) { return false }

        parseLayerNames(layerNames).forEach(l => {
            map.getLayer(l) && map.setFilter(l, filter ?? null)
        })
        return true
    }

    const addImageIcon = (map, img, id) => {
        return new Promise((resolve, reject) => {
            console.log("add image icon", map, img, id)
            if (!map) {return reject("Map not loaded")}
            try {
                map.loadImage(img)
                    .then(image => {
                        console.log(image)
                        map.addImage(id, image.data)
                        resolve()
                    })
                    .catch(reject)
            } catch(e) {
                reject(`Maplibre image no loaded ${e.message}`)
            }
        })
    }

    return {
        getCenterCoordsFromRef,
        pointGeojson,
        pointGeojsonData,
        addPointSource,
        addGeojsonSource,
        addVectorSource,
        addRasterSource,
        addLayer,
        sortLayers,
        removes,
        removeSources,
        removeLayers,
        setVisible,
        zoomInterpolate,
        addClickEvent,
        removeClickEvent,
        setFilter,
        addImageIcon,
        _m,
    }
}

export default MaplibreManager
